import { type ChangeEvent, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Typography,
  Stack,
  Button,
  LoadingButton,
  toast,
} from "@altimateai/lego";
import { IconDownload, IconUpload } from "@tabler/icons-react";

import { useUploadSSOExternalGroups, useUploadSSOGroupMappings } from "./hooks";
import { SSOUploadStatusModal } from "./SSOUploadStatusModal";

const GROUPS_CSV = "group_name\nengineering-team\ndata-platform\n";
const MAPPINGS_CSV =
  "external_group_identifier,match_type,mapping_type,target_name\nengineering-team,exact,team,Data Platform\ndata-analysts,prefix,role,Admin\n";

const downloadTemplate = (content: string, filename: string) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const BulkImportCard = () => {
  const groupsInputRef = useRef<HTMLInputElement>(null);
  const mappingsInputRef = useRef<HTMLInputElement>(null);

  const groupsUpload = useUploadSSOExternalGroups();
  const mappingsUpload = useUploadSSOGroupMappings();

  const groupsResult = groupsUpload.data;
  const mappingsResult = mappingsUpload.data;

  const [groupsSubmittedAt, setGroupsSubmittedAt] = useState<number>();
  const [mappingsSubmittedAt, setMappingsSubmittedAt] = useState<number>();

  const handleGroupsUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Maximum file size is 5MB",
      });
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    groupsUpload.mutate(formData, {
      onSuccess: result => {
        setGroupsSubmittedAt(Date.now());
        if (!result?.failures?.length) {
          toast({
            title: `${result?.created ?? 0} groups imported`,
            description: result?.skipped ? `${result.skipped} already existed` : undefined,
          });
        }
      },
      onError: (err: Error) => {
        toast({ variant: "destructive", title: "Upload failed", description: err.message });
      },
    });
    if (groupsInputRef.current) groupsInputRef.current.value = "";
  };

  const handleMappingsUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Maximum file size is 5MB",
      });
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    mappingsUpload.mutate(formData, {
      onSuccess: result => {
        setMappingsSubmittedAt(Date.now());
        if (!result?.failures?.length) {
          toast({ title: `${result?.created ?? 0} mappings created` });
        }
      },
      onError: (err: Error) => {
        toast({ variant: "destructive", title: "Upload failed", description: err.message });
      },
    });
    if (mappingsInputRef.current) mappingsInputRef.current.value = "";
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Bulk Import</CardTitle>
        </CardHeader>
        <CardContent className="al-space-y-4">
          {/* SSO Groups Upload */}
          <Stack direction="row" className="al-items-center al-justify-between">
            <div>
              <Typography variant="body" className="al-text-sm al-font-medium">
                Import SSO Groups
              </Typography>
              <Typography variant="body" className="al-text-xs al-text-muted-foreground">
                Upload group names from your identity provider (Azure AD / Okta) for autocomplete
                suggestions when adding mappings
              </Typography>
            </div>
            <Stack direction="row" className="al-gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate(GROUPS_CSV, "sso_groups_template.csv")}
              >
                <IconDownload size={14} className="al-mr-1" />
                Groups Template
              </Button>
              <input
                ref={groupsInputRef}
                type="file"
                className="al-hidden"
                accept=".csv"
                aria-label="Upload SSO groups CSV"
                onChange={handleGroupsUpload}
              />
              <LoadingButton
                variant="outline"
                size="sm"
                loading={groupsUpload.isPending}
                onClick={() => groupsInputRef.current?.click()}
              >
                <IconUpload size={14} className="al-mr-1" />
                Upload CSV
              </LoadingButton>
            </Stack>
          </Stack>

          {/* Mappings Upload */}
          <Stack direction="row" className="al-border-t al-pt-4 al-items-center al-justify-between">
            <div>
              <Typography variant="body" className="al-text-sm al-font-medium">
                Import Group Mappings
              </Typography>
              <Typography variant="body" className="al-text-xs al-text-muted-foreground">
                Bulk create team/role mappings from CSV. Use the template for required column
                format.
              </Typography>
            </div>
            <Stack direction="row" className="al-gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate(MAPPINGS_CSV, "sso_mappings_template.csv")}
              >
                <IconDownload size={14} className="al-mr-1" />
                Mappings Template
              </Button>
              <input
                ref={mappingsInputRef}
                type="file"
                className="al-hidden"
                accept=".csv"
                aria-label="Upload mappings CSV"
                onChange={handleMappingsUpload}
              />
              <LoadingButton
                variant="outline"
                size="sm"
                loading={mappingsUpload.isPending}
                onClick={() => mappingsInputRef.current?.click()}
              >
                <IconUpload size={14} className="al-mr-1" />
                Upload CSV
              </LoadingButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <SSOUploadStatusModal
        title="SSO Groups Upload Results"
        created={groupsResult?.created}
        skipped={groupsResult?.skipped}
        reactivated={groupsResult?.reactivated}
        failures={groupsResult?.failures}
        submittedAt={groupsSubmittedAt}
      />
      <SSOUploadStatusModal
        title="Mappings Upload Results"
        created={mappingsResult?.created}
        failures={mappingsResult?.failures}
        submittedAt={mappingsSubmittedAt}
      />
    </>
  );
};
